import * as marietta from '../..';
import { DIMENSION_CORRECTION_FREE, DIMENSION_CORRECTION_MODULE } from '../Constants';

/** ピッチ入力欄の要素ID */
const INPUT_ELEMENT_ID: string = 'modulePitchInput';
/** ドロップダウン開閉ボタン（▼）の要素ID */
const DROPDOWN_BUTTON_ID: string = 'modulePitchComboBtn';
/** ドロップダウン選択肢リストの要素ID */
const DROPDOWN_LIST_ID: string = 'modulePitchComboList';
/** 現在モードの選択肢に付けるCSSクラス（チェックマーク表示） */
const SELECTED_CLASS_NAME: string = 'is-selected';
/** ドロップダウン表示時のdisplay値 */
const DISPLAY_BLOCK: string = 'block';
/** ドロップダウン非表示時のdisplay値 */
const DISPLAY_NONE: string = 'none';
/** ピッチ入力欄の確定キー */
const KEY_ENTER: string = 'Enter';
/** ピッチ入力欄の取消キー */
const KEY_ESCAPE: string = 'Escape';

/**
 * ヘッダーのモジュール選択コンボボックス（ピッチ入力欄＋寸法モードドロップダウン）。
 * DOM参照・イベント配線・選択肢生成・表示同期・ピッチ手入力の確定処理を集約する。
 *
 * - 入力欄：モジュール補正時はピッチ値＋単位(mm)を表示し手入力可。フリー寸法時は「フリー寸法」を表示し編集不可
 * - ドロップダウン：「フリー寸法」「モジュール補正」の2択で寸法モードを切り替える
 * - 変更確定はいずれも ConfirmChangeModuleDialog（はい/いいえ）を経由する
 */
class ModulePitchCombo {
  private readonly inputElement: HTMLInputElement;

  private readonly dropdownButtonElement: HTMLButtonElement;

  private readonly dropdownListElement: HTMLUListElement;

  /**
   * コンストラクタ
   */
  constructor() {
    this.inputElement = document.getElementById(INPUT_ELEMENT_ID) as HTMLInputElement;
    this.dropdownButtonElement = document.getElementById(DROPDOWN_BUTTON_ID) as HTMLButtonElement;
    this.dropdownListElement = document.getElementById(DROPDOWN_LIST_ID) as HTMLUListElement;

    // 入力欄：フォーカスで単位(mm)を外し、フォーカスアウトで確定、Enterで確定（blur経由）、Escapeで取消
    this.inputElement.addEventListener('focus', this.inputFocusEventHandler);
    this.inputElement.addEventListener('blur', this.inputBlurEventHandler);
    this.inputElement.addEventListener('keydown', this.inputKeyDownEventHandler);

    // ▼ボタンでドロップダウン開閉、選択肢クリックで寸法モード変更
    this.dropdownButtonElement.addEventListener('click', this.dropdownButtonClickEventHandler);
    this.dropdownListElement.addEventListener('click', this.dropdownListClickEventHandler);

    // コンボボックス外のクリックでドロップダウンを閉じる
    document.addEventListener('click', this.closeDropdown);
  }

  /**
   * ドロップダウンの選択肢を作成する。
   * 選択肢は「フリー寸法」「モジュール補正」の2択（建物種別に依らずマンション戸建て共通）。
   * ※モジュールピッチの数値は選択肢にせず、撮影アプリ(LiDAR)/保存データ由来の値または入力欄への手入力を使う。
   */
  public createOptions(): void {
    this.dropdownListElement.innerHTML = '';
    const optionDefs: { value: string, label: string }[] = [
      { value: marietta.webcad.WebCADGlobal.MODULE_PITCH_FREE_OPTION, label: marietta.webcad.WebCADGlobal.MODULE_LABEL_FREE },
      { value: marietta.webcad.WebCADGlobal.MODULE_PITCH_MODULE_OPTION, label: marietta.webcad.WebCADGlobal.MODULE_LABEL_MODULE },
    ];
    optionDefs.forEach((optionDef: { value: string, label: string }) => {
      const itemElement = document.createElement('li');
      const buttonElement = document.createElement('button');
      buttonElement.type = 'button';
      buttonElement.value = optionDef.value;
      buttonElement.textContent = optionDef.label;
      itemElement.appendChild(buttonElement);
      this.dropdownListElement.appendChild(itemElement);
    });
  }

  /**
   * コンボボックス（入力欄・ドロップダウン選択肢）の表示を現在の寸法モードへ同期する。
   * フリー寸法時は入力欄に「フリー寸法」を表示して編集不可、
   * モジュール補正時は設定ピッチ値＋単位（例: 910mm）を表示して手入力可にする。
   * ドロップダウンの選択肢には現在モードのチェックマーク（選択クラス）を付け、ドロップダウンは閉じる。
   */
  public syncToCurrentMode(): void {
    const isFree: boolean = marietta.webcad.WebCADGlobal.moduleManager.isFree();
    this.inputElement.value = isFree
      ? marietta.webcad.WebCADGlobal.MODULE_LABEL_FREE
      : String(marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch) + marietta.webcad.WebCADGlobal.MODULE_LABEL_UNIT;
    this.inputElement.readOnly = isFree;

    // 表示同期時はドロップダウンを閉じる（入力確定ダイアログ表示と展開が重ならないように）
    this.closeDropdown();
    const selectedValue: string = isFree
      ? marietta.webcad.WebCADGlobal.MODULE_PITCH_FREE_OPTION
      : marietta.webcad.WebCADGlobal.MODULE_PITCH_MODULE_OPTION;
    this.dropdownListElement.querySelectorAll('button').forEach((buttonElement: HTMLButtonElement) => {
      buttonElement.classList.toggle(SELECTED_CLASS_NAME, buttonElement.value === selectedValue);
    });
  }

  /**
   * 入力欄にフォーカスした（編集開始）
   * 単位(mm)を外した数値のみにする（フリー寸法時はreadonlyのため対象外）
   */
  private inputFocusEventHandler = () => {
    if (!marietta.webcad.WebCADGlobal.moduleManager.isFree()) {
      this.inputElement.value = String(marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch);
    }
  }

  /**
   * 入力欄からフォーカスが外れた（確定）
   */
  private inputBlurEventHandler = () => {
    this.commitInput();
  }

  /**
   * 入力欄でキーが押された
   * Enterで確定（blur経由）、Escapeで入力を破棄して現在値表示へ戻す
   */
  private inputKeyDownEventHandler = (e: KeyboardEvent) => {
    if (e.key === KEY_ENTER) {
      this.inputElement.blur();
    } else if (e.key === KEY_ESCAPE) {
      // 入力を破棄して現在値の表示へ戻す（blur時の確定処理では「変更なし」と判定される）
      this.syncToCurrentMode();
      this.inputElement.blur();
    }
  }

  /**
   * ▼ボタンがクリックされた（ドロップダウン開閉）
   */
  private dropdownButtonClickEventHandler = (e: Event) => {
    // 直後の document への click で閉じられないように伝播を止める
    e.stopPropagation();
    const isOpen: boolean = this.dropdownListElement.style.display === DISPLAY_BLOCK;
    this.dropdownListElement.style.display = isOpen ? DISPLAY_NONE : DISPLAY_BLOCK;
  }

  /**
   * ドロップダウンの選択肢がクリックされた（寸法モード変更）
   */
  private dropdownListClickEventHandler = (e: Event) => {
    const buttonElement = (<HTMLElement>e.target).closest('button') as HTMLButtonElement | null;
    this.closeDropdown();
    if (buttonElement) {
      this.changeDimensionMode(buttonElement.value);
    }
  }

  /**
   * ドロップダウンを閉じる
   */
  private closeDropdown = () => {
    this.dropdownListElement.style.display = DISPLAY_NONE;
  }

  /**
   * 寸法モード（フリー寸法/モジュール補正）の変更
   * @param raw 選択された選択肢の値（MODULE_PITCH_FREE_OPTION / MODULE_PITCH_MODULE_OPTION）
   */
  private changeDimensionMode(raw: string): void {
    const selectFree: boolean = raw === marietta.webcad.WebCADGlobal.MODULE_PITCH_FREE_OPTION;
    if (selectFree === marietta.webcad.WebCADGlobal.moduleManager.isFree()) {
      // 現在と同じ寸法モードの再選択は変更なし（従来のselectのchangeイベント相当。確認ダイアログは出さない）
      return;
    }
    // モーダル背景クリックでは閉じない（「はい」「いいえ」の処理を通さずに閉じると
    // モジュール選択UIの表示だけが変わり、内部の寸法モード・ピッチと不整合になるため）
    const tarDialog = marietta.webcad.WebCADGlobal.showDialog(
      marietta.webcad.view.dialog.ConfirmChangeModuleDialog,
      false,
      true,
      false,
    ) as marietta.webcad.view.dialog.ConfirmChangeModuleDialog;

    if (selectFree) {
      // フリー寸法：寸法モードをfreeへ。ピッチは表示スケールとして現状維持
      tarDialog.targetDimensionCorrection = DIMENSION_CORRECTION_FREE;
      tarDialog.targetModule = marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch;
    } else {
      // モジュール補正：寸法モードをmoduleへ。撮影アプリ(LiDAR)から受け取ったピッチ
      // （手入力で変更済みならその値）をセット
      tarDialog.targetDimensionCorrection = DIMENSION_CORRECTION_MODULE;
      tarDialog.targetModule = marietta.webcad.WebCADGlobal.moduleManager.modulePitchFromApp;
    }
  }

  /**
   * ピッチ入力欄の確定処理。
   * MODULE_PITCH_MIN〜MODULE_PITCH_MAX の数値のみ受け付け、
   * 範囲外・非数・変更なしの場合は表示を現在値へ戻す。有効な変更は確認ダイアログを経て反映する。
   */
  private commitInput(): void {
    if (marietta.webcad.WebCADGlobal.moduleManager.isFree()) {
      // フリー寸法中は編集不可（readonly）のため通常は来ないが、表示だけ現在モードへ同期する
      this.syncToCurrentMode();
      return;
    }
    // 末尾に単位(mm)が付いたまま確定された場合（表示のまま・手入力で付けた場合）も数値として解釈する
    const rawText: string = this.inputElement.value.trim();
    const unitLabel: string = marietta.webcad.WebCADGlobal.MODULE_LABEL_UNIT;
    const numericText: string = rawText.endsWith(unitLabel)
      ? rawText.slice(0, rawText.length - unitLabel.length)
      : rawText;
    const inputValue: number = Number(numericText);
    if (!marietta.webcad.WebCADGlobal.moduleManager.isValidPitch(inputValue)
      || Math.round(inputValue) === marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch) {
      // 範囲外・非数・変更なしは現在値の表示へ戻す
      this.syncToCurrentMode();
      return;
    }
    // モーダル背景クリックでは閉じない（changeDimensionMode と同じ理由）
    const tarDialog = marietta.webcad.WebCADGlobal.showDialog(
      marietta.webcad.view.dialog.ConfirmChangeModuleDialog,
      false,
      true,
      false,
    ) as marietta.webcad.view.dialog.ConfirmChangeModuleDialog;
    tarDialog.targetDimensionCorrection = DIMENSION_CORRECTION_MODULE;
    tarDialog.targetModule = Math.round(inputValue);
  }
}

export default ModulePitchCombo;
